import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Target, Sparkles } from "lucide-react";

export default function GoalsSpotlight() {
  const [yearGoals, setYearGoals] = useState([]);
  const year = new Date().getFullYear();

  const load = async () => {
    const { data } = await api.get("/goals", { params: { scope: "year", period: String(year) } });
    setYearGoals(data);
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    const total = yearGoals.length;
    const done = yearGoals.filter((g) => g.completed).length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return { total, done, pct };
  }, [yearGoals]);

  const remaining = yearGoals.filter((g) => !g.completed).slice(0, 5);

  return (
    <div
      className="rounded-2xl border-2 border-primary/30 bg-card p-5 sm:p-6 shadow-sm"
      data-testid="goals-spotlight"
      style={{
        backgroundImage:
          "radial-gradient(circle at 0% 0%, hsl(var(--primary) / 0.08), transparent 40%)",
      }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <div className="size-9 rounded-xl bg-primary/15 flex items-center justify-center">
            <Target strokeWidth={1.5} className="size-5 text-primary" />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              Your {year} goals
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight">
              {stats.total === 0
                ? "Set your first goal of the year"
                : `${stats.done} of ${stats.total} achieved`}
            </h2>
          </div>
        </div>
        {stats.total > 0 && (
          <div className="flex items-center gap-2">
            <Sparkles strokeWidth={1.5} className="size-4 text-primary" />
            <span className="font-display font-extrabold text-2xl">{stats.pct}%</span>
          </div>
        )}
      </div>

      {stats.total > 0 ? (
        <>
          <Progress value={stats.pct} className="h-2 mb-4" data-testid="goals-progress" />
          {remaining.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {remaining.map((g) => (
                <span
                  key={g.id}
                  className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full border border-border bg-background"
                  data-testid={`spotlight-goal-${g.id}`}
                >
                  <span className="size-1.5 rounded-full bg-primary" />
                  {g.title}
                </span>
              ))}
            </div>
          )}
          {stats.done === stats.total && stats.total > 0 && (
            <p className="text-sm text-muted-foreground">
              Every goal is checked off — incredible work. Add another to keep momentum.
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          Add your first goal to see it featured here every time you open Lumivera.
        </p>
      )}
    </div>
  );
}
